import { DayDate } from '@cmp-src/data-display/dates/DayDate';
import { ResourceLocation } from '@cmp-src/data-display/resource/ResourceLocation';
import { EditIcon } from '@cmp-src/icons/TacticIcons/EditIcon';
import { ViewDeskIcon } from '@cmp-src/icons/TacticIcons/VIewDeskIcon';
import { ISchedule, ISlot } from '@gettactic/api';
import { Button } from '@v2/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@v2/components/ui/dropdown-menu';

import { useAreas, useAuthenticated } from '@gettactic/hooks';
import { useRouter } from 'next/router';
import { isRoom, createMapLink, cn } from '@gettactic/helpers';
import { useReserveModal } from '@/hooks/useReserveModal';
import { RoomData } from './RoomData';
import { RoomActions } from './RoomActions';
import { MapWithMiniOfficePhoto } from './MapWithMiniOfficePhoto';
import { UsersInOffice } from './UsersInOffice';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/api';
import { ReactNode, useCallback } from 'react';
import { getReservationDataFromSlot } from '@gettactic/helpers/src/reservation/getReservationDataFromSlot';
import { ReservationModals } from '@webV2/components/reservation-modals/ReservationModals';
import { getResourceCoverUrl } from '@gettactic/helpers/src/resources/getResourceCoverUrl';
import useResource from '@/hooks/useResource';
import { JoinButton } from '@cmp-src/components/JoinButton/JoinButton';

export type DashboardEventDetailsProps = {
  schedule: ISchedule;
  calendarSwitch?: ReactNode;
};

export const DashboardEventDetails = ({
  schedule: incomingSchedule,
  calendarSwitch
}: DashboardEventDetailsProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { openEditModal, openCancelModal } = useReserveModal();

  const { data: scheduleUpdated } = useQuery({
    queryKey: ['schedule', incomingSchedule.id],
    queryFn: () =>
      api.client.schedules.scheduleById(incomingSchedule.id),
    enabled: !!incomingSchedule.id
  });

  const schedule = scheduleUpdated?.result || incomingSchedule;
  const { resource } = useResource({ id: schedule?.resource.id });
  const officeId = schedule.resource.office_id;
  const parentId = schedule.resource.parent_id;
  const { areas: areaList } = useAreas(officeId);
  const thisOffice = authenticatedUser.offices?.byId[officeId];
  const officeName = thisOffice?.name;
  const areaName = parentId ? areaList?.byId[parentId]?.name : undefined;

  const link = createMapLink({
    date: schedule.start,
    officeId,
    resourceId: schedule.resource?.id,
    floorId: schedule.resource?.parent_id ?? undefined,
    startTime: schedule.start,
    endTime: schedule.end
  });

  if (!schedule) {
    return null;
  }

  const renderAsRoom = isRoom(schedule.resource.type);

  const resourceName = schedule.resource.name;
  const meetingName = schedule.title;
  const eventTitle = renderAsRoom
    ? (meetingName ?? resourceName)
    : resourceName;

  const userSlot = schedule.slots.find(
    (slot) => slot.user.id === authenticatedUser.user?.id
  ) as ISlot;

  const { canEdit, canRespondCancel } = getReservationDataFromSlot(userSlot);

  // Function to handle cancel action
  const handleCancel = useCallback(async () => {
    if (!schedule) return;
    await openCancelModal([schedule]);
  }, [schedule, openCancelModal]);

  const handleEdit = useCallback(() => {
    if (!schedule) return;

    openEditModal(schedule, true);
  }, [schedule, openEditModal]);
  const resourceMediaUrl = resource ? getResourceCoverUrl(resource) : undefined;

  return (
    <div className="mt-16 mb-16 flex w-full flex-col items-center justify-center">
      <div className="flex h-full w-full flex-col">
        <div className="relative flex flex-row items-center justify-between pb-4">
          <div>
            <DayDate
              apiDate={schedule.start}
              tz={schedule.time_zone}
              className="v2-header-1"
            />
          </div>
          <div className="flex gap-2">
            <JoinButton
              iconOnly={true}
              onlineMeeting={schedule.online_meeting ?? null}
              onRefetch={() => {
                queryClient.invalidateQueries(['schedule', schedule.id]);
                queryClient.invalidateQueries(['schedules']);
              }}
              wrapper={(children, icon, props) => (
                <Button
                  variant="outlinePale"
                  {...props}
                  className={cn('flex items-center px-3', props.className)}
                >
                  {icon}
                  {children}
                </Button>
              )}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outlinePale"
                  className="px-3"
                  disabled={!canEdit && !canRespondCancel}
                >
                  <EditIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                  >
                    Manage
                  </DropdownMenuItem>
                )}
                {canRespondCancel && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                  >
                    Remove
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => {
                router.push(link);
              }}
            >
              <ViewDeskIcon />
              Locate
            </Button>
          </div>
        </div>
        {schedule.start && (
          <MapWithMiniOfficePhoto
            resourceId={schedule.resource.id}
            officeId={schedule.resource.office_id}
            parentId={schedule.resource.parent_id ?? ''}
            startDate={schedule.start.split('T')[0]}
            resourceImage={resourceMediaUrl}
          />
        )}
        <div className="flex flex-row items-center justify-between pt-[30px]">
          <div className="flex flex-col justify-between">
            <div className="font-bold text-lg">{eventTitle}</div>
            <ResourceLocation
              location={officeName || 'Location'}
              floor={areaName || 'Floor'}
            />
          </div>
          {renderAsRoom && (
            <RoomActions
              schedule={schedule}
              userSlot={userSlot}
              remoteEnabled={true}
            />
          )}
        </div>
        {renderAsRoom && (
          <div className="pt-12">
            <RoomData schedule={schedule} resource={resource} />
          </div>
        )}
        <div className="pt-[48px]">
          <UsersInOffice officeId={officeId} />
        </div>
      </div>
      <ReservationModals />
    </div>
  );
};
